import React, { useState, useRef, useEffect } from 'react';
import { PincodeDetails } from '../../types/pincodeTypes';
import api from '../../helpers/axios';
import Input from '../bootstrap/forms/Input';
import Card, { CardBody } from '../bootstrap/Card';
import Button from '../bootstrap/Button';
import Spinner from '../bootstrap/Spinner';
import './PincodeSearch.css';

interface PincodeSearchProps {
  onPincodeSelect: (pincodes: PincodeDetails[]) => void;
  selectedPincodes: PincodeDetails[];
  placeholder?: string;
  disabled?: boolean;
  showSelectedCount?: boolean;
}

const PincodeSearch: React.FC<PincodeSearchProps> = ({
  onPincodeSelect,
  selectedPincodes,
  placeholder = "Search pincodes by code, area, city, or district...",
  disabled = false,
  showSelectedCount = true
}) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<PincodeDetails[]>([]);
  const [loading, setLoading] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const searchRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Handle search with debouncing
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (query.trim().length >= 2) {
        searchPincodes(query.trim());
      } else {
        setResults([]);
        setShowResults(false);
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [query]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setShowResults(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const searchPincodes = async (searchQuery: string) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await api.get(`/api/pincodes/search`, {
        params: {
          query: searchQuery,
          limit: 10
        }
      });
      
      const data = response.data;
      
      
      // Handle different response formats
      let pincodes: PincodeDetails[] = [];
      
      if (Array.isArray(data)) {
        // Direct array response
        pincodes = data;
      } else if (data.content && Array.isArray(data.content)) {
        // Paginated response
        pincodes = data.content;
      } else {
        
      }
      
      
      setResults(pincodes);
      setShowResults(pincodes.length > 0);
    } catch (err: any) {
      console.error('Pincode search failed:', err);
      setError('Failed to search pincodes. Please try again.');
      setResults([]);
      setShowResults(false);
    } finally {
      setLoading(false);
    }
  };

  const handleSelect = (pincode: PincodeDetails) => {
    // Check if already selected
    const isSelected = selectedPincodes.some(p => p.pincodeId === pincode.pincodeId);
    if (!isSelected) {
      onPincodeSelect([...selectedPincodes, pincode]);
    }
    
    // Clear search
    setQuery('');
    setResults([]);
    setShowResults(false);
    
    // Focus back to input for easy multiple selections
    inputRef.current?.focus();
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setQuery(e.target.value);
  };

  const handleInputFocus = () => {
    if (results.length > 0) {
      setShowResults(true);
    }
  };

  return (
    <div className="position-relative" ref={searchRef}>
      <div className="d-flex align-items-center gap-2">
        <div className="flex-grow-1">
          <Input
            ref={inputRef}
            type="text"
            value={query}
            onChange={handleInputChange}
            onFocus={handleInputFocus}
            placeholder={placeholder}
            disabled={disabled}
            className="form-control"
          />
        </div>
        {loading && (
          <Spinner size="sm" color="primary" />
        )}
        {showSelectedCount && selectedPincodes.length > 0 && (
          <span className="badge bg-primary">{selectedPincodes.length}</span>
        )}
      </div>

      {error && (
        <div className="alert alert-danger alert-sm mt-2" role="alert">
          {error}
        </div>
      )}

      {showResults && results.length > 0 && (
        <Card className="position-absolute w-100 mt-1 shadow-lg" style={{ zIndex: 1000, maxHeight: '300px', overflowY: 'auto' }}>
          <CardBody className="p-0">
            {results.map((pincode, index) => {
              const isAlreadySelected = selectedPincodes.some(p => p.pincodeId === pincode.pincodeId);
              
              return (
                <div
                  key={pincode.pincodeId}
                  className={`p-3 border-bottom cursor-pointer hover-bg-light ${isAlreadySelected ? 'bg-light text-muted' : ''}`}
                  onClick={() => !isAlreadySelected && handleSelect(pincode)}
                  style={{ 
                    cursor: isAlreadySelected ? 'not-allowed' : 'pointer',
                    borderBottom: index === results.length - 1 ? 'none' : '1px solid #dee2e6'
                  }}
                >
                  <div className="d-flex justify-content-between align-items-start">
                    <div className="flex-grow-1">
                      <div className="fw-bold text-primary">
                        {pincode.pincode}
                        {isAlreadySelected && (
                          <span className="badge bg-success ms-2">Selected</span>
                        )}
                      </div>
                      <div className="text-dark">{pincode.areaName}</div>
                      <small className="text-muted">
                        {pincode.cityName ? `${pincode.cityName}, ` : ''}{pincode.districtName}, {pincode.stateName}
                      </small>
                      {pincode.officeName && (
                        <div className="text-muted" style={{ fontSize: '0.75rem' }}>
                          {pincode.officeName} ({pincode.officeType})
                        </div>
                      )}
                    </div>
                    <div className="text-end">
                      <small className="text-muted">{pincode.stateCode}</small>
                      {pincode.deliveryStatus && (
                        <div className={`badge ms-1 ${pincode.deliveryStatus === 'Delivery' ? 'bg-success' : 'bg-warning'}`} style={{ fontSize: '0.6rem' }}>
                          {pincode.deliveryStatus}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </CardBody>
        </Card>
      )}

      {showResults && results.length === 0 && !loading && query.trim().length >= 2 && (
        <Card className="position-absolute w-100 mt-1 shadow-lg" style={{ zIndex: 1000 }}>
          <CardBody className="text-center text-muted py-3">
            No pincodes found for "{query}"
          </CardBody>
        </Card>
      )}
    </div>
  );
};

export default PincodeSearch;
